import { Card } from "@/components/ui/card";
import {
  TypographyH1,
  TypographyH3,
  TypographyP,
} from "@/components/ui/typography";
import { fallbackLng } from "@/lib/i18n/settings";
import {
  BookText,
  Briefcase,
  GraduationCap,
  Heart,
  Lightbulb,
  Target,
} from "lucide-react";
import Link from "next/link";
import { HeaderBg } from "../HeaderBg";

export const resumeModules = [
  {
    id: 1,
    name: "Summaries",
    slug: "summaries",
    icon: BookText,
    description:
      "A brief overview of your qualifications, skills, and career highlights.",
  },
  {
    id: 2,
    name: "Objectives",
    slug: "objectives",
    icon: Target,
    description:
      "A concise statement outlining your career goals and what you aim to achieve.",
  },
  {
    id: 3,
    name: "Skills",
    slug: "skills",
    icon: Lightbulb,
    description:
      "A list of your core competencies and technical abilities relevant to the role.",
  },
  {
    id: 4,
    name: "Work Experience",
    slug: "work-experience",
    icon: Briefcase,
    description:
      "Details of your past roles, responsibilities, and accomplishments.",
  },
  {
    id: 5,
    name: "Education",
    slug: "education",
    icon: GraduationCap,
    description:
      "Information about your academic background, degrees, and certifications.",
  },
  {
    id: 6,
    name: "Interests",
    slug: "interests",
    icon: Heart,
    description:
      "Personal hobbies or activities that showcase your personality or values.",
  },
];

export default function ResumeExamplesCategoriesSection({
  lng = fallbackLng,
  job = "",
}: {
  lng?: string;
  job?: string;
}) {
  const formatJobTitle = (slug: string) => {
    return decodeURIComponent(slug)
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const jobTitle = formatJobTitle(job);

  return (
    <div>
      <HeaderBg>
        <div className="flex h-[322px] flex-col items-center justify-center px-4 text-center">
          <TypographyH1>
            {jobTitle ? `${jobTitle} Resume Examples` : "Resume Examples"}
          </TypographyH1>
          <TypographyP className="mt-[10px]">
            Optimize your {jobTitle} resume with our proven examples and samples
            to ensure your resume can stand out among other competitors.
          </TypographyP>
        </div>
      </HeaderBg>

      <div className="mx-auto max-w-[1280px] px-4 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {resumeModules.map((module) => {
            const Icon = module.icon;
            return (
              <Card
                key={module.id}
                className="group border-2 border-transparent bg-white transition-all hover:border-emerald-500 hover:bg-emerald-50"
              >
                <Link
                  href={`/resume-examples/${decodeURIComponent(job)}/${module.slug}`}
                  className="flex flex-col items-center p-8"
                >
                  <div className="mb-4 rounded-full bg-emerald-100 p-4 text-emerald-600 transition-colors group-hover:bg-emerald-200">
                    <Icon className="h-8 w-8" />
                  </div>
                  <TypographyH3 className="mb-1 text-xl font-semibold">
                    {module.name}
                  </TypographyH3>
                  <TypographyP className="text-center text-sm text-gray-600">
                    {module.description}
                  </TypographyP>
                </Link>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
